import { apiClient } from './api';
import {
  Campaign,
  CreateCampaignData,
  UpdateCampaignData,
  CampaignFilters,
  CampaignResultFilters,
  CampaignStats,
  CampaignPermanentDeleteStatus,
  Voice,
  PhoneNumber,
  PhoneNumberPurpose,
  VapiPhoneNumber,
} from '../types/campaign';

export const campaignService = {
  // Get all campaigns
  async getCampaigns(filters?: CampaignFilters): Promise<{ campaigns: Campaign[]; total: number }> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.status?.length) {
        filters.status.forEach(status => params.append('status', status));
      }
      if (filters.excludeStatus) params.append('excludeStatus', filters.excludeStatus);
      if (filters.search) params.append('search', filters.search);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
    }

    const response = await apiClient.get<{ campaigns: Campaign[]; total: number }>(
      `/campaigns?${params.toString()}`
    );
    return response.data.data;
  },

  // Get all campaigns for admin
  async getAllCampaignsForAdmin(filters?: CampaignFilters): Promise<{
    campaigns: (Campaign & { userEmail: string; userName: string })[];
    total: number;
  }> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.status?.length) {
        filters.status.forEach(status => params.append('status', status));
      }
      if (filters.excludeStatus) params.append('excludeStatus', filters.excludeStatus);
      if (filters.search) params.append('search', filters.search);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
    }

    const response = await apiClient.get<{
      campaigns: (Campaign & { userEmail: string; userName: string })[];
      total: number;
    }>(
      `/admin/campaigns?${params.toString()}`
    );
    return response.data.data;
  },

  // Get campaign by ID
  async getCampaign(id: string): Promise<Campaign> {
    const response = await apiClient.get<any>(`/campaigns/${id}`);
    return response.data.data.campaign;
  },

  // Create new campaign
  async createCampaign(data: CreateCampaignData): Promise<Campaign> {
    const response = await apiClient.post<any>('/campaigns', data);
    return response.data.data.campaign;
  },

  // Update campaign
  async updateCampaign(id: string, data: UpdateCampaignData): Promise<Campaign> {
    const response = await apiClient.patch<any>(`/campaigns/${id}`, data);
    return response.data.data.campaign;
  },

  // Delete campaign
  async deleteCampaign(id: string): Promise<void> {
    await apiClient.delete(`/campaigns/${id}`);
  },

  // Restore campaign from trash
  async restoreCampaign(id: string): Promise<Campaign> {
    const response = await apiClient.post<any>(`/campaigns/${id}/restore`);
    return response.data.data.campaign;
  },

  // Permanently delete campaign
  async permanentlyDeleteCampaign(id: string): Promise<{ jobId: string; campaignId: string }> {
    const response = await apiClient.delete<any>(`/campaigns/${id}/permanent`);
    return response.data.data;
  },

  async getPermanentDeleteStatus(id: string): Promise<CampaignPermanentDeleteStatus | null> {
    const response = await apiClient.get<any>(`/campaigns/${id}/permanent-delete-status`);
    return (response.data?.data ?? null) as CampaignPermanentDeleteStatus | null;
  },

  // Start campaign
  async startCampaign(id: string): Promise<Campaign> {
    const response = await apiClient.post<any>(`/campaigns/${id}/start`);
    return response.data.data.campaign;
  },

  // Pause campaign
  async pauseCampaign(id: string): Promise<Campaign> {
    const response = await apiClient.post<any>(`/campaigns/${id}/pause`);
    return response.data.data.campaign;
  },

  // Complete campaign
  async completeCampaign(id: string): Promise<Campaign> {
    const response = await apiClient.post<any>(`/campaigns/${id}/complete`);
    return response.data.data.campaign;
  },

  // Restart campaign
  async restartCampaign(id: string): Promise<Campaign> {
    const response = await apiClient.post<any>(`/campaigns/${id}/restart`);
    return response.data.data.campaign;
  },

  // Get campaign statistics
  async getCampaignStats(id: string, filters?: CampaignResultFilters): Promise<CampaignStats> {
    const response = await apiClient.get<any>(`/campaigns/${id}/stats`, { params: filters });
    return response.data.data.stats;
  },

  // Clone campaign
  async cloneCampaign(id: string, name: string): Promise<Campaign> {
    const response = await apiClient.post<Campaign>(`/campaigns/${id}/clone`, { name });
    return response.data.data;
  },

  // Duplicate campaign with conversation styles
  async duplicateCampaign(id: string, name?: string): Promise<Campaign> {
    const response = await apiClient.post<any>(`/campaigns/${id}/duplicate`, { name });
    return response.data.data.campaign;
  },

  // Export campaign data
  async exportCampaign(id: string, format: 'csv' | 'xlsx' = 'csv'): Promise<void> {
    await apiClient.download(`/campaigns/${id}/export?format=${format}`, `campaign-${id}.${format}`);
  },

  // Bulk start all draft/paused campaigns
  async startAllCampaigns(): Promise<{ started: string[]; failed: { id: string; error: string }[] }> {
    const response = await apiClient.post<any>('/campaigns/bulk/start-all');
    return response.data.data;
  },

  // Bulk stop (pause) all active campaigns
  async stopAllCampaigns(): Promise<{ stopped: string[]; failed: { id: string; error: string }[] }> {
    const response = await apiClient.post<any>('/campaigns/bulk/stop-all');
    return response.data.data;
  },

  async updateSmsSequenceSteps(
    campaignId: string,
    steps: Array<{ stepNumber: number; templateBody: string; delayHours: number }>
  ): Promise<void> {
    await apiClient.put(`/sms/campaigns/${campaignId}/sequence-steps`, { steps });
  },



  // OpenAI Integration
  // Get OpenAI settings
  async getOpenAISettings(): Promise<{ hasApiKey: boolean; isConnected: boolean; lastTestedAt?: string; maskedApiKey?: string }> {
    const response = await apiClient.get<{ hasApiKey: boolean; isConnected: boolean; lastTestedAt?: string; maskedApiKey?: string }>('/providers/openai');
    return response.data.data;
  },

  // Save OpenAI settings
  async saveOpenAISettings(data: { apiKey: string }): Promise<void> {
    await apiClient.post('/providers/openai', data);
  },

  // Test OpenAI connection
  async testOpenAIConnection(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>('/providers/openai/test');
    return response.data.data;
  },

  // Disconnect OpenAI (delete credentials)
  async disconnectOpenAI(): Promise<void> {
    await apiClient.delete('/providers/openai');
  },

  // Telnyx Integration
  async getTelnyxSettings(): Promise<{ hasApiKey: boolean; isConnected: boolean; lastTestedAt?: string; maskedApiKey?: string; maskedConnectionId?: string }> {
    const response = await apiClient.get<{ hasApiKey: boolean; isConnected: boolean; lastTestedAt?: string; maskedApiKey?: string; maskedConnectionId?: string }>('/providers/telnyx');
    return response.data.data;
  },

  async saveTelnyxSettings(data: { apiKey: string; connectionId: string }): Promise<void> {
    await apiClient.post('/providers/telnyx', data);
  },

  async testTelnyxConnection(data?: { apiKey?: string; connectionId?: string }): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>('/providers/telnyx/test', data);
    return response.data.data;
  },

  async placeTestCall(fromNumber: string, toNumber: string): Promise<{ success: boolean; data: { callControlId?: string; message: string } }> {
    const response = await apiClient.post<any>('/providers/telnyx/test-call', { fromNumber, toNumber });
    return response.data;
  },

  async getTelnyxPhoneNumbers(): Promise<{ phoneNumbers: Array<{ id: string; phoneNumber: string; connectionId: string; status: string }> }> {
    const response = await apiClient.get<any>('/providers/telnyx/phone-numbers');
    return response.data.data;
  },

  /**
   * Get phone numbers from Telnyx, mapped to PhoneNumber format
   * so they can be used in Campaign and Admin pages seamlessly.
   */
  async getVapiPhoneNumbers(): Promise<VapiPhoneNumber[]> {
    const response = await apiClient.get<any>('/providers/vapi/phone-numbers');
    return response.data.data || [];
  },

  async importPhoneNumberToVapi(phoneNumber: string, label?: string): Promise<VapiPhoneNumber> {
    const response = await apiClient.post<any>('/providers/vapi/phone-numbers/import', {
      phoneNumber,
      label,
    });
    return response.data.data;
  },

  async getPhoneNumbers(): Promise<PhoneNumber[]> {
    const telnyxNumbers = await this.getTelnyxPhoneNumbers();
    const numbers = Array.isArray(telnyxNumbers) ? telnyxNumbers : (telnyxNumbers as any)?.phoneNumbers || [];
    return numbers.map((n: any) => ({
      id: n.id,
      number: n.phone_number || n.phoneNumber || '',
      provider: 'telnyx',
      providerId: n.connection_id || n.connectionId || '',
      smsCapable: Boolean(n.sms_capable ?? n.smsCapable ?? false),
      messagingProfileId: n.messaging_profile_id || n.messagingProfileId || undefined,
      purpose: n.purpose ?? null,
      assignedCarrier: (n.assigned_carrier || n.assignedCarrier || 'all') as PhoneNumber['assignedCarrier'],
      connectionName: (n.connection_name || n.connectionName || '').trim() || undefined,
      connectionOk: n.connection_ok ?? n.connectionOk,
      needsConnectionFix: n.needs_connection_fix ?? n.needsConnectionFix,
      name: (n.connection_name || n.connectionName || n.phone_number || n.phoneNumber || '').trim() || undefined,
      createdAt: n.created_at || new Date().toISOString(),
      updatedAt: n.updated_at,
    }));
  },

  async fixTelnyxPhoneNumberConnections(includeVapi = false): Promise<{
    fixed: string[];
    expectedConnectionName: string;
    skipped: Array<{ phoneNumber: string; reason: string }>;
  }> {
    const response = await apiClient.post<any>('/providers/telnyx/phone-numbers/fix-connections', {
      includeVapi,
    });
    return response.data.data;
  },

  /** Assign a line type to a Telnyx number (null = unassign). */
  async setPhoneNumberPurpose(
    id: string,
    purpose: PhoneNumberPurpose | null
  ): Promise<void> {
    await apiClient.patch(`/providers/telnyx/phone-numbers/${id}/purpose`, { purpose });
  },

  async setPhoneNumberAssignedCarrier(
    id: string,
    assignedCarrier: import('../types/campaign').PhoneNumberAssignedCarrier
  ): Promise<void> {
    await apiClient.patch(`/providers/telnyx/phone-numbers/${id}/assigned-carrier`, {
      assignedCarrier,
    });
  },

  async disconnectTelnyx(): Promise<void> {
    await apiClient.delete('/providers/telnyx');
  },

  // VAPI Integration
  async getVapiSettings(): Promise<{
    hasApiKey: boolean;
    isConnected: boolean;
    lastTestedAt?: string;
    maskedPublicApiKey?: string;
    maskedPrivateApiKey?: string;
  }> {
    const response = await apiClient.get<{
      hasApiKey: boolean;
      isConnected: boolean;
      lastTestedAt?: string;
      maskedPublicApiKey?: string;
      maskedPrivateApiKey?: string;
    }>('/providers/vapi');
    return response.data.data;
  },

  async saveVapiSettings(data: { publicApiKey: string; privateApiKey: string }): Promise<void> {
    await apiClient.post('/providers/vapi', data);
  },

  async testVapiConnection(data?: { publicApiKey?: string; privateApiKey?: string }): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>('/providers/vapi/test', data);
    return response.data.data;
  },

  async disconnectVapi(): Promise<void> {
    await apiClient.delete('/providers/vapi');
  },

  /** All Vapi voice-library voices (11labs, vapi, …). Requires Vapi credentials. */
  async getVapiVoices(): Promise<Voice[]> {
    const response = await apiClient.get<{ voices: Voice[] }>('/providers/vapi/voices');
    return response.data.data.voices || [];
  },

  async getVoiceProviders(): Promise<string[]> {
    const response = await apiClient.get<{ providers: string[] }>('/providers/vapi/voice-providers');
    return response.data.data.providers || [];
  },

  async getVoicesByProvider(provider: string): Promise<Voice[]> {
    const response = await apiClient.get<{ voices: Voice[] }>(
      `/providers/vapi/voices/${encodeURIComponent(provider)}`
    );
    return response.data.data.voices || [];
  },

  /** Curated Telnyx NaturalHD voices for Telnyx Assistant (legacy). */
  async getTelnyxVoices(): Promise<Voice[]> {
    const response = await apiClient.get<{ voices: Voice[] }>('/providers/telnyx/voices');
    return response.data.data.voices || [];
  },

  /** ElevenLabs voices via Telnyx — Custom AI (conversation_relay) pipeline v2. */
  async getCustomAiElevenLabsVoices(): Promise<{ voices: Voice[]; warning?: string }> {
    const response = await apiClient.get<{ voices: Voice[]; warning?: string }>(
      '/providers/telnyx/elevenlabs-voices'
    );
    return {
      voices: response.data.data.voices || [],
      warning: response.data.data.warning,
    };
  },

  /** Fish Audio voices — self-hosted Custom AI only. */
  async getFishAudioVoices(): Promise<{ voices: Voice[]; warning?: string }> {
    const response = await apiClient.get<{ voices: Voice[]; warning?: string }>(
      '/providers/fish-audio/voices'
    );
    return {
      voices: response.data.data.voices || [],
      warning: response.data.data.warning,
    };
  },

  // Get campaign templates
  async getCampaignTemplates(): Promise<any[]> {
    const response = await apiClient.get('/campaigns/templates');
    return response.data.data;
  },

  // Create campaign from template
  async createCampaignFromTemplate(templateId: string, data: {
    name: string;
    description?: string;
  }): Promise<Campaign> {
    const response = await apiClient.post<any>(`/campaigns/templates/${templateId}`, data);
    return response.data.data.campaign;
  },

  // Make test call
  async makeTestCall(campaignId: string, phoneNumber: string): Promise<{ success: boolean; message?: string; callId?: string }> {
    const response = await apiClient.post<any>(`/campaigns/${campaignId}/test-call`, { phoneNumber });
    return response.data;
  },

  async importTestLeads(campaignId: string, count: 10 | 20): Promise<{ requested: number; imported: number }> {
    const response = await apiClient.post<any>(`/campaigns/${campaignId}/import-test-leads`, { count });
    return response.data.data;
  },

  async clearTestLeads(campaignId: string): Promise<{ found: number; deleted: number }> {
    const response = await apiClient.post<any>(`/campaigns/${campaignId}/clear-test-leads`);
    return response.data.data;
  },

  async syncAreaCodes(campaignId: string): Promise<{ totalUpdated: number }> {
    const response = await apiClient.post<any>(`/campaigns/${campaignId}/sync-area-codes`);
    return response.data.data;
  },

  async getAreaCodeCounts(
    campaignId: string,
    timezone: 'Eastern' | 'Central' | 'Mountain' | 'Pacific'
  ): Promise<{
    timezone: string;
    totalLeads: number;
    areaCodes: Array<{ areaCode: string; count: number }>;
  }> {
    const response = await apiClient.get(`/campaigns/${campaignId}/area-codes`, {
      params: { timezone },
    });
    return response.data.data;
  },

  // Get enhanced prompt script for debugging
  async getEnhancedPrompt(campaignId: string): Promise<string | null> {
    try {
      const response = await apiClient.get<any>(`/campaigns/${campaignId}/enhanced-prompt`);
      return response.data.data.enhancedPrompt;
    } catch (error) {
      console.error('Failed to fetch enhanced prompt:', error);
      return null;
    }
  },

  // Transfer campaign ownership
  async transferOwnership(campaignId: string, newOwnerId: string): Promise<Campaign> {
    const response = await apiClient.patch<any>(`/campaigns/${campaignId}/transfer-ownership`, {
      newOwnerId
    });
    return response.data.data.campaign;
  },

  // Get campaign counts by status
  async getCampaignCounts(): Promise<{ [status: string]: number; total: number }> {
    const response = await apiClient.get<{ [status: string]: number; total: number }>('/campaigns/counts');
    return response.data.data;
  },

  // Get campaign counts for admin
  async getCampaignCountsForAdmin(): Promise<{ [status: string]: number; total: number }> {
    const response = await apiClient.get<{ [status: string]: number; total: number }>('/admin/campaigns/counts');
    return response.data.data;
  },

  async listTelnyxAssistants(): Promise<{ id: string; name?: string; model?: string }[]> {
    const response = await apiClient.get<{ id: string; name?: string; model?: string }[]>(
      '/campaigns/telnyx-assistants'
    );
    return response.data.data ?? [];
  },
};
